import React, { useState } from 'react';

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import BookingAuthorityOtpDialog from './BookingAuthorityOtpDialog';
import { getPhone } from './LuxLoyaltyInfiniteProfileDetails.utils';

interface Props {
  bookingAuthorities: App.LuxLoyaltyInfiniteBookingAuthority[];
  buttonSx?: object;
  buttonVariant?: 'text' | 'outlined' | 'contained';
  label?: string;
}

export default function BookingAuthorityOtpButton({
  bookingAuthorities,
  buttonSx,
  buttonVariant = 'contained',
  label = 'Verify Booking Authority SMS',
}: Props) {
  const [isAuthorityPickerOpen, setIsAuthorityPickerOpen] = useState(false);
  const [otpAuthority, setOtpAuthority] = useState<App.LuxLoyaltyInfiniteBookingAuthority | null>(null);
  const activeBookingAuthorities = bookingAuthorities.filter((authority) => !authority.isDeactivated);

  const startAuthorityVerification = (authority: App.LuxLoyaltyInfiniteBookingAuthority) => {
    setIsAuthorityPickerOpen(false);
    setOtpAuthority(authority);
  };

  return (
    <>
      <Button
        size="small"
        variant={buttonVariant}
        disabled={activeBookingAuthorities.length === 0}
        onClick={() => setIsAuthorityPickerOpen(true)}
        sx={buttonSx}
      >
        {label}
      </Button>
      <Dialog open={isAuthorityPickerOpen} onClose={() => setIsAuthorityPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select booking authority</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {bookingAuthorities.length > 0 ? (
              bookingAuthorities.map((authority, index) => (
                <Paper key={authority.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                    spacing={1.5}
                  >
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip size="small" variant="outlined" label={`Booking authority ${index + 1}`} />
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {[authority.title, authority.firstName, authority.lastName].filter(Boolean).join(' ')}
                        </Typography>
                        {authority.isDeactivated && <Chip size="small" color="default" label="Deactivated" />}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {authority.email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getPhone(authority)}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!!authority.isDeactivated}
                      onClick={() => startAuthorityVerification(authority)}
                    >
                      Verify SMS
                    </Button>
                  </Stack>
                </Paper>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No booking authorities linked.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAuthorityPickerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <BookingAuthorityOtpDialog
        authority={otpAuthority}
        open={Boolean(otpAuthority)}
        onClose={() => setOtpAuthority(null)}
        onVerified={() => setOtpAuthority(null)}
      />
    </>
  );
}
